import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { FEES, FIRM, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "What is an off-the-plan contract in Queensland?",
    a: "It is a contract to buy a lot that does not legally exist yet — an apartment, townhouse or vacant lot in a plan that has not been registered with the Titles Registry. You sign and pay a deposit now, and settlement happens weeks after the plan registers, which can be a year or more away.",
  },
  {
    q: "What is a sunset date and can the developer cancel my contract?",
    a: "The sunset date is the long-stop date by which the plan must be registered. If registration has not happened by then, the contract can usually be terminated by either party. In a rising market that has been used to cancel contracts and re-sell the same lot at a higher price, so the sunset clause and any developer termination rights are the first thing we read.",
  },
  {
    q: "How long is the cooling-off period on an off-the-plan contract?",
    a: "A standard Queensland residential contract carries a five business day cooling-off period, and terminating during it costs 0.25% of the purchase price. Legal advice before you sign is far better protection than relying on cooling off, because the disclosure regime for off-the-plan lots gives you separate and stronger rights.",
  },
  {
    q: "What disclosure am I entitled to before signing?",
    a: "For a proposed lot in a community titles scheme the seller must give a disclosure statement before you sign, including the proposed lot entitlements, the body corporate budget, expected levies and the proposed by-laws. If that disclosure is inaccurate in a material way, or the final plan differs materially from what was disclosed, you may have a right to terminate. Those rights are time-limited.",
  },
  {
    q: "When do I pay transfer duty on an off-the-plan purchase?",
    a: "Duty is generally assessed on the contract and payable within 30 days of the contract becoming unconditional, not at settlement — which surprises buyers on long builds. Home and first home concessions depend on your circumstances at the time and on moving in within the required period after settlement, so we work the timing through with you early.",
  },
  {
    q: "Will my finance approval still be valid at settlement?",
    a: "Usually not. Approvals commonly lapse well before the plan registers, and the lender will re-value the lot at completion. If the valuation comes in under the contract price you have to fund the gap. We flag the re-approval timing at the start so it is not discovered the week before settlement.",
  },
  {
    q: "What does off-the-plan conveyancing cost with Coastside Law?",
    a: `Our fixed fee for a residential purchase is ${FEES.purchase} including GST, including off-the-plan and house and land contracts, with disbursements at cost. Pre-signing contract review is quoted separately and credited against the conveyancing fee if you proceed with us.`,
  },
];

const RISKS = [
  {
    title: "Sunset dates and developer termination",
    body: "Registration timing drives your settlement date, your finance validity and your duty. We identify who can walk away, when, and what you get back if they do.",
  },
  {
    title: "Variation and substitution clauses",
    body: "Most developer contracts allow the finished lot to differ from the plan — area tolerances, finishes, car space location, even lot numbering. We tell you how far they can move before you can object.",
  },
  {
    title: "Disclosure and material prejudice",
    body: "Proposed lot entitlements, levies, by-laws and the community management statement. If the final scheme differs materially from what was disclosed, there are time-limited termination rights worth knowing about.",
  },
  {
    title: "Body corporate budgets and levies",
    body: "First-year budgets in new schemes are often understated. We look at the sinking fund, the building management and caretaking agreements and any embedded network or letting arrangements.",
  },
  {
    title: "Deposit security and bank guarantees",
    body: "Where the deposit is held, whether interest accrues to you, and whether a deposit bond or bank guarantee is accepted in place of cash.",
  },
  {
    title: "House and land — two contracts, not one",
    body: "The land contract settles on registration; the build contract carries its own progress payments, provisional sums, variation pricing and delay rights. We review both together.",
  },
];

const STEPS = [
  {
    t: "Before you sign",
    d: "Send us the contract, the disclosure statement and the plans. We give you a written review covering the sunset date, variation rights, disclosure, levies and duty timing, and what to negotiate while you still have leverage.",
  },
  {
    t: "During the build",
    d: "We hold the file open, diarise the sunset date and disclosure deadlines, and tell you when to start re-confirming finance rather than waiting for the developer's notice.",
  },
  {
    t: "On registration",
    d: "Registration triggers a short settlement window — commonly 14 to 21 days. We check the registered plan against what was disclosed, review the final community management statement and raise any material difference immediately.",
  },
  {
    t: "Settlement and inspection",
    d: "Pre-settlement inspection of the finished lot, defect list to the developer, adjustments for rates, levies and water, and electronic settlement with your lender.",
  },
];

export const Route = createFileRoute("/off-the-plan-contracts")({
  head: () => ({
    meta: [
      { title: fitTitle("Off the Plan Contract Review Queensland | Coastside Law") },
      {
        name: "description",
        content: fitDescription(`Queensland off-the-plan and house and land contract lawyers. Sunset dates, disclosure, levies and settlement on registration. Fixed fee ${FEES.purchase} incl. GST. Call ${FIRM.phone}.`),
      },
      { property: "og:title", content: fitTitle(`Off the Plan Contracts Queensland | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Solicitor review of off-the-plan and house and land contracts in Queensland — sunset dates, disclosure, body corporate levies and settlement on registration."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/off-the-plan-contracts" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "/off-the-plan-contracts" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: OffThePlanPage,
});

function OffThePlanPage() {
  return (
    <>
      <PageHero
        eyebrow="Conveyancing"
        title="Off the plan contracts"
        lead="Buying off the plan means signing a developer's contract for a lot that does not exist yet, then waiting a year or more to settle. The contract is drafted to protect the developer. We read it before you sign, and act on the purchase at a fixed fee."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="font-serif text-3xl text-foreground">
          Why off-the-plan contracts need a solicitor, not a signature
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          An ordinary house purchase is a standard REIQ contract with a settlement date six weeks
          away. An off-the-plan purchase is a bespoke developer document with a sunset date, a
          disclosure regime under the Body Corporate and Community Management Act, variation rights,
          and a settlement date that nobody can name at the time you sign.
        </p>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Most of the risk sits in clauses you can still negotiate before signing and cannot touch
          afterwards. That is why the useful hour is at the start — not when registration lands and
          you have fourteen days to settle.
        </p>

        <h2 className="mt-16 font-serif text-3xl text-foreground">What we look for</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {RISKS.map((item) => (
            <div key={item.title} className="rounded-sm border border-border bg-card p-5">
              <h3 className="font-serif text-lg text-foreground">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.body}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">How the matter runs</h2>
        <ol className="mt-6 space-y-4">
          {STEPS.map((s, i) => (
            <li key={s.t} className="flex gap-4 rounded-sm border border-border bg-card p-5">
              <span className="font-serif text-2xl text-accent-foreground/70">{i + 1}</span>
              <div>
                <h3 className="font-serif text-lg text-foreground">{s.t}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-16 rounded-sm border-l-2 border-accent bg-secondary/50 p-6">
          <h2 className="font-serif text-2xl text-foreground">Fixed fee</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Off-the-plan and house and land purchases are handled on our standard purchase fee of{" "}
            {FEES.purchase} including GST, with disbursements at cost — even though the file stays
            open far longer than an ordinary conveyance. Pre-signing contract review is quoted
            separately and credited against the conveyancing fee if you go ahead with us.
          </p>
          <div className="mt-5 flex flex-wrap gap-4 text-sm font-semibold">
            <Link to="/fees" className="text-primary underline">
              All fixed fees and disbursements
            </Link>
            <Link to="/conveyancing" className="text-primary underline">
              Conveyancing
            </Link>
            <Link to="/baringa" className="text-primary underline">
              Baringa &amp; Aura new builds
            </Link>
          </div>
        </div>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who does the work" />

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Off the plan questions we are asked
        </h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <div className="mt-16">
          <EnquiryForm heading="Send us your off-the-plan contract" />
        </div>
      </section>

      <CtaBand
        title="Have a contract in front of you?"
        body={`Do not sign it yet. Call ${FIRM.phone} or send it through and we will tell you what you are committing to and what to negotiate.`}
      />
      <div className="h-20" />
    </>
  );
}
